import React, { useState, useEffect } from 'react';
import { 
  ShieldExclamationIcon, 
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Textarea from '../../components/ui/Textarea';
import { massActionsService, MassAction, MassActionType, MassActionStatus } from '../../services/massActionsService';

const MassActionsPage: React.FC = () => {
  const [actions, setActions] = useState<MassAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedActionType, setSelectedActionType] = useState<MassActionType>('BAN_USERS');
  const [targetIds, setTargetIds] = useState('');
  const [reason, setReason] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadActions();
  }, [page, filterStatus]);

  const loadActions = async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (filterStatus !== 'all') params.status = filterStatus;

      const result = await massActionsService.list(params);
      setActions(result.actions);
      setTotalPages(result.pagination.totalPages);
    } catch (error: any) {
      console.error('Failed to load mass actions:', error);
      toast.error(error.response?.data?.message || 'Failed to load mass actions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!targetIds.trim() || !reason.trim()) {
      toast.error('Please provide target IDs and reason');
      return;
    }

    const ids = targetIds
      .split(/[\n,]/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (ids.length === 0) {
      toast.error('No valid target IDs provided');
      return;
    }

    if (ids.length > 1000) {
      toast.error('Maximum 1000 items per mass action');
      return;
    }

    if (!window.confirm(`Are you sure you want to ${selectedActionType.replace(/_/g, ' ').toLowerCase()} ${ids.length} items?\n\nReason: ${reason}\n\nThis action cannot be undone.`)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = {
        actionType: selectedActionType,
        targetIds: ids,
        reason,
      };

      if (selectedActionType === 'SEND_NOTIFICATION' && notificationMessage) {
        payload.notificationMessage = notificationMessage;
      }

      const result = await massActionsService.create(payload);
      toast.success(`Mass action initiated: ${result.massAction.id}`);
      
      // Reset form
      setTargetIds('');
      setReason('');
      setNotificationMessage('');
      
      // Reload actions list
      await loadActions();
    } catch (error: any) {
      console.error('Failed to create mass action:', error);
      toast.error(error.response?.data?.message || 'Failed to create mass action');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Are you sure you want to cancel this mass action?')) {
      return;
    }

    try {
      await massActionsService.cancel(id);
      toast.success('Mass action cancelled');
      await loadActions();
    } catch (error: any) {
      console.error('Failed to cancel mass action:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel mass action');
    }
  };

  const getStatusIcon = (status: MassActionStatus) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case 'FAILED':
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      case 'PROCESSING':
        return <ClockIcon className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'PENDING':
        return <ClockIcon className="w-5 h-5 text-yellow-600" />;
      case 'CANCELLED':
        return <ExclamationTriangleIcon className="w-5 h-5 text-gray-600" />;
      default:
        return null;
    }
  };

  const getActionTypeLabel = (type: MassActionType): string => {
    return type.replace(/_/g, ' ');
  };

  const actionTypes: MassActionType[] = [
    'BAN_USERS',
    'UNBAN_USERS',
    'SUSPEND_USERS',
    'DELETE_POSTS',
    'HIDE_POSTS',
    'RESTORE_POSTS',
    'REFUND_PAYMENTS',
    'DELETE_COMMENTS',
    'SEND_NOTIFICATION',
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mass Admin Actions</h1>
          <p className="text-gray-600 mt-2">
            Perform bulk operations on users, posts, comments, and payments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ShieldExclamationIcon className="w-8 h-8 text-red-600" />
        </div>
      </div>

      {/* Create New Mass Action */}
      <Card className="mb-8">
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Mass Action</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action Type</label>
              <select
                value={selectedActionType}
                onChange={(e) => setSelectedActionType(e.target.value as MassActionType)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                {actionTypes.map((type) => (
                  <option key={type} value={type}>
                    {getActionTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target IDs (one per line or comma-separated)
              </label>
              <Textarea
                rows={6}
                value={targetIds}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTargetIds(e.target.value)}
                placeholder="user123&#10;user456&#10;user789"
                helperText={`Enter up to 1000 IDs. Current count: ${targetIds.split(/[\n,]/).filter((id) => id.trim().length > 0).length}`}
              />
            </div>

            {selectedActionType === 'SEND_NOTIFICATION' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notification Message</label>
                <Textarea
                  rows={3}
                  value={notificationMessage}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotificationMessage(e.target.value)}
                  placeholder="Enter the notification message to send to all selected users..."
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason (Required)</label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
                placeholder="Enter the reason for this mass action (minimum 10 characters)..."
                helperText="This will be logged in the audit trail"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setTargetIds('');
                  setReason('');
                  setNotificationMessage('');
                }}
              >
                Clear
              </Button>
              <Button
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={!targetIds.trim() || !reason.trim() || reason.trim().length < 10}
              >
                Execute Mass Action
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mass Actions History */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Mass Actions History</h2>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading mass actions...</p>
            </div>
          ) : actions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No mass actions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {actions.map((action) => (
                <div key={action.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusIcon(action.status)}
                        <span className="font-semibold text-gray-900">
                          {getActionTypeLabel(action.actionType)}
                        </span>
                        <span className="text-sm text-gray-500">
                          by {action.admin?.username}
                        </span>
                        <span className="text-sm text-gray-400">
                          {new Date(action.createdAt).toLocaleString()}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-2">
                        <span className="font-medium">Reason:</span> {action.reason}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Total: {action.totalTargets}</span>
                        <span>Processed: {action.processedCount}</span>
                        <span className="text-green-600">Success: {action.successCount}</span>
                        <span className="text-red-600">Failed: {action.failedCount}</span>
                      </div>

                      {action.status === 'PROCESSING' && (
                        <div className="mt-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{
                                width: `${(action.processedCount / action.totalTargets) * 100}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="ml-4">
                      {(action.status === 'PENDING' || action.status === 'PROCESSING') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancel(action.id)}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MassActionsPage;
